import { memo, useCallback, useState } from "react";

import { Card, CardContent } from "@/components/Card/card";
import { FilePreviewModal } from "@/components/FilePreview/FilePreviewModal";
import Icon from "@/components/Icon/Icon";
import { HStack, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { Button } from "@/components/ui/button";
import { useHighlyConfidentialFileContent } from "@/features/highly-confidential";
import { downloadFileDirectly } from "@/lib/api/utils";
import {
  CONFIDENTIAL_FILE_ICON,
  isHighlyConfidentialFile,
} from "@/lib/security/confidential-utils";
import { formatFileSize } from "@/utils/number/si-prefix";

import type { FileInfo, UploadedFormDataFileInfo } from "./types";

interface ReadOnlyFileListProps {
  fileInfos: readonly FileInfo[];
}

export const ReadOnlyFileList = memo(({ fileInfos }: ReadOnlyFileListProps) => {
  const [previewFile, setPreviewFile] =
    useState<UploadedFormDataFileInfo | null>(null);

  // 高機密ファイルの内容を取得
  const {
    data: confidentialFileUrl,
    isLoading: confidentialFileLoading,
    error: confidentialFileError,
  } = useHighlyConfidentialFileContent(
    previewFile?.s3Url || "",
    previewFile?.originalname
      ? decodeURIComponent(previewFile.originalname)
      : "",
    previewFile?.mimetype || "",
    {
      enabled:
        !!previewFile && isHighlyConfidentialFile(previewFile?.s3Url || ""),
    },
  );

  // 高機密ファイルプレビュー用のURL取得関数
  const getConfidentialPreviewFileUrl = useCallback(() => {
    return confidentialFileUrl || "";
  }, [confidentialFileUrl]);

  const getFileName = (info: FileInfo): string => {
    if (info.type === "local") {
      return info.file.name;
    } else {
      return decodeURIComponent(info.fileInfo.originalname);
    }
  };

  const getFileUrl = (info: FileInfo): string => {
    if (info.type === "local") {
      return URL.createObjectURL(info.file);
    } else {
      return info.fileInfo.s3Url;
    }
  };

  const getFileSize = (info: FileInfo): number => {
    if (info.type === "local") {
      return info.file.size;
    } else {
      return info.fileInfo.size;
    }
  };

  const getPreviewImage = (info: FileInfo): string | undefined => {
    if (info.type === "local") {
      return info.preview;
    } else if (
      info.type === "uploaded" &&
      info.fileInfo.mimetype.startsWith("image/") &&
      !isHighlyConfidentialFile(info.fileInfo.s3Url)
    ) {
      return info.fileInfo.s3Url;
    }
    return undefined;
  };

  const onDownloadFile = (fileName: string, url: string) => {
    downloadFileDirectly(url, fileName);
  };

  return (
    <>
      <VStack className="gap-2">
        {fileInfos.map((fileInfo) => {
          const previewImage = getPreviewImage(fileInfo);

          return (
            <Card key={fileInfo.id} className="w-full">
              <CardContent className="p-3">
                <HStack className="gap-3 items-center">
                  {previewImage ? (
                    <img
                      src={previewImage}
                      alt="preview"
                      className="w-10 h-10 object-cover rounded border"
                    />
                  ) : (
                    <div className="w-10 h-10 flex items-center justify-center">
                      <Icon
                        iconName={
                          fileInfo.type === "uploaded" &&
                          fileInfo.fileInfo.mimetype.startsWith("image/") &&
                          isHighlyConfidentialFile(fileInfo.fileInfo.s3Url)
                            ? CONFIDENTIAL_FILE_ICON
                            : "FileXIcon"
                        }
                        className={
                          fileInfo.type === "uploaded" &&
                          fileInfo.fileInfo.mimetype.startsWith("image/") &&
                          isHighlyConfidentialFile(fileInfo.fileInfo.s3Url)
                            ? "w-6 h-6 text-neutral-muted-on-fill"
                            : "w-10 h-10 text-neutral-muted-on-fill"
                        }
                      />
                    </div>
                  )}
                  <VStack className="flex-1 gap-1">
                    <Text variant="labelSmall" className="truncate">
                      {getFileName(fileInfo)}
                    </Text>
                    <Text
                      variant="labelSmallReg"
                      className="text-neutral-muted-on-fill"
                    >
                      {formatFileSize(getFileSize(fileInfo))}
                    </Text>
                  </VStack>
                  {fileInfo.type === "uploaded" &&
                  isHighlyConfidentialFile(fileInfo.fileInfo.s3Url) ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPreviewFile(fileInfo.fileInfo)}
                    >
                      <Icon iconName="Eye" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        onDownloadFile(
                          getFileName(fileInfo),
                          getFileUrl(fileInfo),
                        )
                      }
                    >
                      <Icon iconName="Download" />
                    </Button>
                  )}
                </HStack>
              </CardContent>
            </Card>
          );
        })}
      </VStack>

      {/* 高機密ファイルプレビューモーダル */}
      {previewFile && (
        <FilePreviewModal
          isOpen={true}
          onClose={() => setPreviewFile(null)}
          filename={decodeURIComponent(previewFile.originalname)}
          mimeType={previewFile.mimetype}
          onGetFileUrl={getConfidentialPreviewFileUrl}
          isLoading={confidentialFileLoading}
          error={confidentialFileError?.message || null}
        />
      )}
    </>
  );
});

ReadOnlyFileList.displayName = "ReadOnlyFileList";
